// frontend/src/components/client/mobile/MobileClientDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Fuel, Wrench, CreditCard, Calendar, Home, User, MessageSquare, Warehouse } from 'lucide-react';
import { getDashboardStats } from '../../../services/clientApi';
import { useClientAuth } from '../../../contexts/ClientAuthContext';

import MobileFuelRequests from './MobileFuelRequests';
import MobileMaintenanceRequests from './MobileMaintenanceRequests';
import MobileInvoices from './MobileInvoices';
import MobileHangars from './MobileHangars';
import MobileProfile from './MobileProfile';
import MobileChat from './MobileChat';

const GLOBAL_STYLE = `
  @keyframes spin { to { transform: rotate(360deg) } }
  @keyframes slideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
  * { -webkit-tap-highlight-color: transparent; }
`;

// ─── Tab Bar ─────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'home',        label: 'Home',   Icon: Home },
  { id: 'fuel',        label: 'Fuel',   Icon: Fuel },
  { id: 'maintenance', label: 'Maint.', Icon: Wrench },
  { id: 'invoices',    label: 'Invoices', Icon: CreditCard },
  { id: 'profile',     label: 'Profile', Icon: User },
];

const TabBar = ({ active, onChange }) => (
  <nav style={{
    position: 'fixed', bottom: 0, left: 0, right: 0,
    background: 'rgba(10,15,30,0.97)',
    borderTop: '0.5px solid #1e293b',
    display: 'flex',
    paddingBottom: 'env(safe-area-inset-bottom)',
    zIndex: 100,
    backdropFilter: 'blur(16px)',
  }}>
    {TABS.map(({ id, label, Icon }) => {
      const on = active === id;
      return (
        <button key={id} onClick={() => onChange(id)} style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 3, padding: '10px 0 8px',
          background: 'none', border: 'none', cursor: 'pointer',
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: on ? 'rgba(59,130,246,0.18)' : 'transparent',
            transition: 'background 0.2s',
          }}>
            <Icon size={20} color={on ? '#3b82f6' : '#475569'} strokeWidth={on ? 2.2 : 1.8} />
          </div>
          <span style={{ fontSize: 10, fontWeight: on ? 600 : 400, color: on ? '#3b82f6' : '#475569' }}>{label}</span>
        </button>
      );
    })}
  </nav>
);

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, Icon, accent, onClick }) => (
  <button onClick={onClick} style={{
    background: '#1e293b', borderRadius: 18, border: '0.5px solid #334155',
    padding: '14px 12px', textAlign: 'left', cursor: 'pointer',
    display: 'flex', flexDirection: 'column', gap: 8, width: '100%',
    transition: 'transform 0.12s',
  }}
    onTouchStart={e => e.currentTarget.style.transform = 'scale(0.96)'}
    onTouchEnd={e => e.currentTarget.style.transform = 'scale(1)'}
  >
    <div style={{ width: 34, height: 34, borderRadius: 10, background: `${accent}22`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={17} color={accent} />
    </div>
    <div>
      <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent, lineHeight: 1 }}>{value}</div>
    </div>
  </button>
);

// ─── Quick Action ─────────────────────────────────────────────────────────────
const QA = ({ Icon, label, accent, onClick }) => (
  <button onClick={onClick} style={{
    flex: 1, background: '#1e293b', border: '0.5px solid #334155',
    borderRadius: 16, padding: '12px 6px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
    cursor: 'pointer', transition: 'transform 0.12s',
  }}
    onTouchStart={e => e.currentTarget.style.transform = 'scale(0.94)'}
    onTouchEnd={e => e.currentTarget.style.transform = 'scale(1)'}
  >
    <div style={{ width: 36, height: 36, borderRadius: 10, background: `${accent}22`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={18} color={accent} />
    </div>
    <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500, textAlign: 'center', lineHeight: 1.2 }}>{label}</span>
  </button>
);

// ─── Activity Item ────────────────────────────────────────────────────────────
const STATUS_MAP = {
  pending:   { label: 'Pending', bg: 'rgba(234,179,8,0.15)',  color: '#eab308' },
  approved:  { label: 'Approved',   bg: 'rgba(34,197,94,0.15)', color: '#22c55e' },
  completed: { label: 'Completed',    bg: 'rgba(59,130,246,0.15)',color: '#3b82f6' },
  paid:      { label: 'Paid',      bg: 'rgba(34,197,94,0.15)', color: '#22c55e' },
  rejected:  { label: 'Rejected',     bg: 'rgba(239,68,68,0.15)', color: '#ef4444' },
};

const ActivityItem = ({ Icon, iconColor, title, subtitle, status }) => {
  const s = STATUS_MAP[status] || STATUS_MAP.pending;
  return (
    <div style={{ background: '#1e293b', borderRadius: 16, border: '0.5px solid #334155', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 38, height: 38, borderRadius: 11, background: `${iconColor}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={17} color={iconColor} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{subtitle}</div>
      </div>
      <span style={{ fontSize: 10, fontWeight: 600, padding: '4px 9px', borderRadius: 20, background: s.bg, color: s.color, flexShrink: 0 }}>{s.label}</span>
    </div>
  );
};

// ─── Home Screen ──────────────────────────────────────────────────────────────
const HomeScreen = ({ stats, statsLoading, client, onNavigate }) => {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? '🌅 Good morning' : hour < 18 ? '☀️ Good afternoon' : '🌙 Good evening';
  const initials = `${client?.first_name?.[0] || ''}${client?.last_name?.[0] || ''}`.toUpperCase();

  return (
    <div style={{ padding: '0 16px 100px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Hero banner */}
      <div style={{ background: 'linear-gradient(135deg, #1e40af 0%, #1e293b 100%)', borderRadius: 22, padding: '20px', border: '0.5px solid #2d4a7a' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 13, color: '#93c5fd', marginBottom: 3 }}>{greeting}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>
              {client?.company_name || `${client?.first_name} ${client?.last_name}`}
            </div>
          </div>
          <div style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#fff' }}>
            {initials || <User size={20} color="#fff" />}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => onNavigate('fuel')} style={{ background: 'rgba(255,255,255,0.12)', border: '0.5px solid rgba(255,255,255,0.2)', borderRadius: 12, padding: '8px 16px', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            ⛽ Fuel
          </button>
          <button onClick={() => onNavigate('maintenance')} style={{ background: 'rgba(255,255,255,0.12)', border: '0.5px solid rgba(255,255,255,0.2)', borderRadius: 12, padding: '8px 16px', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            🔧 Maintenance
          </button>
        </div>
      </div>

      {/* Stats grid */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Overview</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <StatCard label="Fuel pending"  value={statsLoading ? '…' : (stats?.fuel_requests_pending ?? 0)}         Icon={Fuel}       accent="#fb923c" onClick={() => onNavigate('fuel')} />
          <StatCard label="Maintenance"       value={statsLoading ? '…' : (stats?.maintenance_requests_pending ?? 0)}   Icon={Wrench}     accent="#f87171" onClick={() => onNavigate('maintenance')} />
          <StatCard label="Invoices due"     value={statsLoading ? '…' : (stats?.invoices_pending_count ?? 0)}         Icon={CreditCard} accent="#facc15" onClick={() => onNavigate('invoices')} />
          <StatCard label="Events"        value={statsLoading ? '…' : (stats?.upcoming_events ?? 0)}                Icon={Calendar}   accent="#60a5fa" onClick={() => onNavigate('home')} />
        </div>
      </div>

      {/* Quick actions */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Quick actions</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <QA Icon={Fuel}          label="Fuel"   accent="#fb923c" onClick={() => onNavigate('fuel')} />
          <QA Icon={Wrench}        label="Maintenance" accent="#f87171" onClick={() => onNavigate('maintenance')} />
          <QA Icon={MessageSquare} label="Support"     accent="#34d399" onClick={() => onNavigate('chat')} />
          <QA Icon={Warehouse}     label="Hangars"     accent="#a78bfa" onClick={() => onNavigate('hangars')} />
        </div>
      </div>

      {/* Recent activity */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Recent activity</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <ActivityItem Icon={Fuel}       iconColor="#fb923c" title="Fuel – N-1234AB"   subtitle="150 gallons · 2h ago"  status="pending" />
          <ActivityItem Icon={CreditCard} iconColor="#3b82f6" title="Invoice #INV-0042"       subtitle="$1,240.00 · yesterday"         status="paid" />
          <ActivityItem Icon={Wrench}     iconColor="#f87171" title="Maintenance – N-5678CD" subtitle="High priority · Apr 28" status="approved" />
        </div>
      </div>
    </div>
  );
};

// ─── Page Titles ──────────────────────────────────────────────────────────────
const PAGE_TITLES = {
  fuel: 'Fuel', maintenance: 'Maintenance',
  invoices: 'Invoices', hangars: 'Hangars',
  chat: 'Support', profile: 'Profile',
};

// ─── Root Component ───────────────────────────────────────────────────────────
const MobileClientDashboard = () => {
  const { client, hasPackage } = useClientAuth();
  const [tab, setTab] = useState('home');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try { const r = await getDashboardStats(); setStats(r.data); }
    catch (e) { console.error('Stats:', e); }
    finally { setStatsLoading(false); }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const renderContent = () => {
    switch (tab) {
      case 'home':        return <HomeScreen stats={stats} statsLoading={statsLoading} client={client} onNavigate={setTab} />;
      case 'fuel':        return hasPackage('fuel_online')  ? <MobileFuelRequests />        : null;
      case 'maintenance': return hasPackage('maintenance')  ? <MobileMaintenanceRequests /> : null;
      case 'invoices':    return <MobileInvoices />;
      case 'hangars':     return hasPackage('hangar_lease') ? <MobileHangars />             : null;
      case 'chat':        return <MobileChat />;
      case 'profile':     return <MobileProfile />;
      default:            return null;
    }
  };

  return (
    <div style={{
      minHeight: '100dvh', background: '#0f172a',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
      WebkitFontSmoothing: 'antialiased', color: '#fff',
    }}>
      <style>{GLOBAL_STYLE}</style>

      {/* Safe area top */}
      <div style={{ height: 'env(safe-area-inset-top)' }} />

      {/* Sub-page sticky header */}
      {tab !== 'home' && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 50,
          background: 'rgba(10,15,30,0.97)',
          borderBottom: '0.5px solid #1e293b',
          padding: '14px 20px',
          backdropFilter: 'blur(16px)',
        }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>
            {PAGE_TITLES[tab]}
          </h1>
        </div>
      )}

      {tab === 'home' && <div style={{ height: 8 }} />}

      <main>{renderContent()}</main>

      <TabBar active={tab} onChange={setTab} />
    </div>
  );
};

export default MobileClientDashboard;